import { useEffect } from 'react';

import { services } from '@/services';
import { useAuthStore } from '@/stores/authStore';

/**
 * App 啟動時跑一次 session 探查。
 * -----------------------------------------------------------------------------
 * 流程:
 *
 * 1. 從 sessionStorage 還原回的 ``creativeUid`` 為 ``null``:直接結束 hydrating,
 *    使用者會被導去 ``/login``(沒有 sessionStorage 痕跡 = 從沒在這 tab 登入過,
 *    cookie 也應該空,跳 /login 重新走 GIS one-tap)。
 * 2. 有 ``creativeUid``:呼叫 ``services.authProvider.isAuthenticated()`` 跟後端
 *    用 cookie 確認 session 是否仍有效。
 *    - ``true``:保留 store;UI 顯示 ``user`` 對應頁面。
 *    - ``false`` 或丟例外:清除 store 防止 stale data。
 * 3. 不論結果,最後一定 ``finishHydrating()`` 解除 loading。
 *
 * **R42.5**:guard 從 `accessToken` 改 `creativeUid`(對齊 backend MR !26 收尾砍
 * envelope.access_token + R23.1 cookie-only 認證設計;`creativeUid` 是 sessionStorage
 * 持久化的另一個 identity field,語意等價)。
 *
 * 此 hook **只應在 App 根元件呼叫一次**;多次呼叫會多打後端但不會造成 bug。
 *
 * @example
 *   function App() {
 *     useAuthBootstrap();
 *     return <RouterProvider router={router} />;
 *   }
 */
export function useAuthBootstrap(): void {
  useEffect(() => {
    let cancelled = false;
    const { creativeUid, clear, finishHydrating } = useAuthStore.getState();

    if (!creativeUid) {
      finishHydrating();
      return;
    }

    void (async () => {
      try {
        const ok = await services.authProvider.isAuthenticated();
        if (cancelled) return;
        if (!ok) clear();
      } catch {
        if (!cancelled) clear();
      } finally {
        if (!cancelled) useAuthStore.getState().finishHydrating();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
}
